import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  Container
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import adCenterApi from '../adCenterApi';
import { showToast } from '../CoreDataService';

export default function EditSpendCaps() {
  const location = useLocation();
  const navigate = useNavigate();
  const spendCap = location.state?.spendCap || {};

  const [capName, setCapName] = useState(spendCap.spendCapsName || '');
  const [dailyCapAmount, setDailyCapAmount] = useState(spendCap.spendCapsAmount || '');
  const [currency, setCurrency] = useState(spendCap.currency || 'USD');
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!capName.trim()) newErrors.capName = 'Name is required.';
    if (!dailyCapAmount || isNaN(dailyCapAmount) || Number(dailyCapAmount) <= 0) newErrors.dailyCapAmount = 'Amount must be greater than 0.';
    if (!currency) newErrors.currency = 'Currency is required.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    const payload = {
      adminUser: localStorage.getItem('uuid'),
      brokerId: localStorage.getItem('brokerId'),
      spendCapsId: spendCap.spendCapsId,
      spendCapsName: capName,
      spendCapsAmount: parseFloat(dailyCapAmount),
      currency: currency,
      action: 'UPDATE',
    };
    try {
      const response = await adCenterApi.post('/modifySpendCap', payload);
      if (response.data && response.data.statusCode === 0) {
        showToast('success', 'Spend cap updated successfully!');
        navigate('/viewSpendCaps');
      } 
      else if (response.data && response.data.statusCode === 4) {
        showToast('success', response.data.message);
        navigate('/viewSpendCaps');
      } 
      else {
        showToast('error', response.data.message || 'Failed to update spend cap');
      }
    } catch (err) {
      showToast('error', 'Error updating spend cap');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mb: 6 }}>
      <Box sx={{ width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, mt: 1 }}>
          <Box>
            <Typography variant="h4" component="h1" fontWeight="bold">
              Edit Spend Cap
            </Typography>
            <Typography variant="body1" color="text.secondary" mt={0.5}>
              Update your spend cap details.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => navigate('/viewSpendCaps')}
            sx={{ height: 48, fontWeight: 'bold', textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            View Spend Caps
          </Button>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', mx: 'auto', width: '100%' }}>
          <Card elevation={4} sx={{ borderRadius: 2 }}>
            <CardContent sx={{ p: { xs: 2, md: 4 } }}>
              <Typography variant="h5" fontWeight="bold" color="text.primary" gutterBottom>
                Spend Cap Details
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Edit the daily spending limit for your campaigns.
              </Typography>
              <Box mb={3}>
                <TextField
                  fullWidth
                  label="Name"
                  placeholder="What do you want to call it"
                  variant="outlined"
                  value={capName}
                  onChange={(e) => setCapName(e.target.value)}
                  error={!!errors.capName}
                  helperText={errors.capName}
                />
              </Box>
              <Box mb={4}>
                <TextField
                  fullWidth
                  label="Spend Cap Amount"
                  type="number"
                  placeholder="10"
                  variant="outlined"
                  value={dailyCapAmount}
                  onChange={(e) => setDailyCapAmount(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FormControl variant="standard" sx={{ m: 0, minWidth: 60 }}>
                          <Select
                            value={currency}
                            onChange={(e) => setCurrency(e.target.value)}
                            disableUnderline
                            sx={{
                              '& .MuiSelect-select': {
                                py: '8px',
                                px: '4px !important',
                                pr: '16px !important',
                              },
                              '& .MuiSvgIcon-root': {
                                right: -5
                              }
                            }}
                          >
                            <MenuItem value="USD">USD</MenuItem>
                            <MenuItem value="EUR">EUR</MenuItem>
                            <MenuItem value="GBP">GBP</MenuItem>
                            <MenuItem value="INR">INR</MenuItem>
                            <MenuItem value="USDB">USDB</MenuItem>

                          </Select>
                        </FormControl>
                      </InputAdornment>
                    ),
                    inputProps: { min: 0 }
                  }}
                  error={!!errors.dailyCapAmount}
                  helperText={errors.dailyCapAmount || 'Enter the maximum amount of USDB you wish to spend per day.'}
                />
              </Box>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                onClick={handleSave}
                sx={{ textTransform: 'uppercase', py: 1.5 }}
              >
                Save Changes
              </Button>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Container>
  );
}
